import {
    type App,
    computed,
    inject,
    type Ref,
    ref,
    watch,
    type WritableComputedRef,
} from 'vue'
import type {
    RingSize,
    SignatureStone,
    StoneUri,
} from '@/plugins/ring-builder.ts'
import type { CookieProvider } from '@/entry-server-types.ts'

export interface RingBuilderCookieStore {
    stoneUri: WritableComputedRef<StoneUri | null>
    ringSizePersisted: WritableComputedRef<RingSize | null>
    signatureStone: WritableComputedRef<SignatureStone | undefined>
}

interface RingBuilderState {
    stoneUri: StoneUri | null
    ringSizePersisted: RingSize | null
    signatureStone: SignatureStone | undefined
}

const key = 'app.plugin.store.ring-builder'

const cookieName = 'ringBuilder'

const defaultState: RingBuilderState = {
    stoneUri: null,
    ringSizePersisted: null,
    signatureStone: undefined,
}

function getInitialState(cookies: CookieProvider): RingBuilderState {
    const cookie = cookies.get(cookieName)

    if (!cookie) {
        return defaultState
    }

    try {
        const state = JSON.parse(cookie)

        if (typeof state !== 'object') {
            return defaultState
        }

        return {
            ...defaultState,
            ...state,
        }
    } catch {
        return defaultState
    }
}

export function installRingBuilderStore(
    app: App,
    cookies: CookieProvider
): RingBuilderCookieStore {
    const state: Ref<RingBuilderState> = ref(getInitialState(cookies))

    const store: RingBuilderCookieStore = {
        ringSizePersisted: computed({
            get() {
                return state.value.ringSizePersisted
            },
            set(v: RingSize | null) {
                state.value.ringSizePersisted = v
            },
        }),
        stoneUri: computed({
            get() {
                return state.value.stoneUri
            },
            set(v: StoneUri | null) {
                state.value.stoneUri = v
            },
        }),
        signatureStone: computed({
            get() {
                return state.value.signatureStone
            },
            set(v: SignatureStone | undefined) {
                state.value.signatureStone = v
            },
        }),
    }

    watch(state, (n) => {
        cookies.set(cookieName, JSON.stringify(n), undefined)
    })

    app.provide(key, store)

    return store
}

export function useRingBuilderStore(): RingBuilderCookieStore {
    return inject(key) as RingBuilderCookieStore
}
